/**
 * [TD-47.6c E1-CORRECTION] BidConfidenceLive - slider-only wrapper.
 *
 * Replaces the BidCockpit composition. Per founder's E1-correction:
 * the CompactActionPanel keeps ONLY the Bid Confidence slider during
 * BIDDING. Radar / gold-silver / weekly bar / PGF Analysis all live
 * in the Zone 2 PlayerAnalysisCanvas → "PGF Analytics" tab.
 *
 * Fetches:
 *   • /analytics/players/{id}/canvas?league_id=...  → pgf_value
 *   • /draft/{league}/player-analysis               → roster_fit.fit_score
 *
 * Then drives the slider via useBidConfidence.
 *
 * Alpha-flag gated identically to the rest of the analytics surface;
 * non-alpha users see nothing here (no paywall blur on the cockpit -
 * the paywall lives on the Zone 2 tab where the conversion mechanism
 * is intentional).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { authHeaders, useAuth } from "../../contexts/AuthContext";
import { useEntitlements } from "../../hooks/useEntitlements";
import { BidConfidenceSlider } from "./BidConfidenceSlider";
import { useBidConfidence } from "./useBidConfidence";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BidConfidenceLive({
  leagueId,
  nomination,
  currentBid,
  userBudget,
  userRosterFilled,
  rosterSize,
}) {
  const { user } = useAuth();
  const { hasDraftAnalytics } = useEntitlements();
  const analyticsEnabled =
    user?.role === "admin" || !!user?.alpha_flags?.pgf_analytics_alpha || hasDraftAnalytics;

  const playerId = nomination?.player?.id;
  const position = nomination?.player?.position;

  const [pgfValue, setPgfValue] = useState(null);
  const [rosterFit, setRosterFit] = useState(null);

  useEffect(() => {
    if (!analyticsEnabled || !playerId || !leagueId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/analytics/players/${playerId}/canvas`,
          {
            params: { season_year: 2024, scoring_format: "half_ppr", league_id: leagueId },
            headers: authHeaders(),
          },
        );
        if (!cancelled) setPgfValue(r.data?.pgf_value?.dollars ?? null);
      } catch {
        if (!cancelled) setPgfValue(null);
      }
    })();
    return () => { cancelled = true; };
  }, [analyticsEnabled, playerId, leagueId]);

  useEffect(() => {
    if (!analyticsEnabled || !playerId || !leagueId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(
          `${API}/draft/${leagueId}/player-analysis`,
          { player_id: playerId },
          { headers: authHeaders() },
        );
        if (!cancelled) setRosterFit(r.data?.roster_fit?.fit_score ?? null);
      } catch {
        if (!cancelled) setRosterFit(null);
      }
    })();
    return () => { cancelled = true; };
  }, [analyticsEnabled, playerId, leagueId]);

  const bc = useBidConfidence({
    pgfValue,
    currentBid,
    userBudget,
    userRosterFilled,
    rosterSize,
    position,
    rosterFitScore: rosterFit,
    enabled: analyticsEnabled,
  });

  if (!analyticsEnabled || !bc) return null;

  return (
    <div data-testid="bid-confidence-live" className="w-full">
      <BidConfidenceSlider
        bcScore={bc.score}
        bcLabel={bc.label}
        maxBid={bc.maxBid}
        reasonCode={bc.reasonCode}
      />
    </div>
  );
}

export default BidConfidenceLive;
